import { useState, type ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { ArrowLeft, ExternalLink, FileText, Pencil } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TooltipProvider } from '@/components/ui/tooltip'
import { FlagChips, InvoiceStatus, PlanBadge, RoleBadge, StateBadge, SyncStatus } from '@/components/data/badges'
import { UsageBar } from '@/components/data/meters'
import { PlanDialog } from '@/components/data/plan-dialog'
import { MonoId } from '@/components/data/mono-id'
import { Person } from '@/components/data/person'
import { EmptyState, ErrorState, Warnings } from '@/components/data/states'
import { formatDate, formatDateTime, formatMoney, formatNumber, formatRelative, percent } from '@/lib/format'
import { flagLabels, sourceLabels, statusLabels } from '@/lib/labels'
import { useTeamDetail } from '@/lib/queries'
import type { Breakdown, TeamDetail } from '@/lib/types'

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'members', label: 'Members' },
  { id: 'data', label: 'Data' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'billing', label: 'Billing' },
  { id: 'ai', label: 'AI assistant' },
] as const
type TabId = (typeof TABS)[number]['id']

function Section({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="panel p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="font-semibold">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  )
}

function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="divide-y">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-start justify-between gap-4 py-2.5">
          <dt className="shrink-0 text-muted-foreground">{label}</dt>
          <dd className="min-w-0 text-right">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function OverviewTab({ d }: { d: TeamDetail }) {
  const t = d.team
  const s = t.subscription
  return (
    <div className="grid items-start gap-4 lg:grid-cols-2">
      <Section title="Usage against the plan">
        <div className="space-y-4">
          <div>
            <div className="mb-1 flex justify-between text-muted-foreground">
              <span>Companies</span>
              <span className="figures">{t.limits.companies ? percent(t.companies_usage) : ''}</span>
            </div>
            <UsageBar value={t.companies} limit={t.limits.companies} />
          </div>
          <div>
            <div className="mb-1 flex justify-between text-muted-foreground">
              <span>Members</span>
              <span className="figures">{t.limits.users ? percent(t.members_usage) : ''}</span>
            </div>
            <UsageBar value={t.members} limit={t.limits.users} />
          </div>
          <div>
            <div className="mb-1 text-muted-foreground">Integrations</div>
            <UsageBar value={(t.hubspot ? 1 : 0) + (t.zapier ? 1 : 0)} limit={t.limits.integrations} />
          </div>
        </div>
      </Section>

      <Section title="Subscription">
        <Facts
          rows={[
            ['Plan', <PlanBadge key="p" planId={t.plan_id} name={t.plan_name} trial={s.source === 'trial'} />],
            ['State', <StateBadge key="s" state={s.state} />],
            ...(s.state !== 'free' && s.source === 'trial'
              ? ([
                  ['Granted', 'Trial, as every new team'],
                  [s.state === 'expired' ? 'Ended' : 'Ends', `${formatDate(s.expires_at)} (${formatRelative(s.expires_at)})`],
                ] as [string, ReactNode][])
              : s.state !== 'free' && s.source === 'manual'
              ? ([
                  ['Granted', 'By hand'],
                  ['Until', s.expires_at ? `${formatDate(s.expires_at)} (${formatRelative(s.expires_at)})` : 'No end date'],
                ] as [string, ReactNode][])
              : s.state !== 'free'
                ? ([
                    [s.state === 'canceling' ? 'Ends' : s.state === 'expired' ? 'Ended' : 'Renews', `${formatDate(s.expires_at)} (${formatRelative(s.expires_at)})`],
                    ['Value per month', formatMoney(s.monthly_cents)],
                    ['Stripe customer', <MonoId key="c" value={s.stripe_customer_id} />],
                    ['Stripe subscription', <MonoId key="sub" value={s.stripe_subscription_id} />],
                  ] as [string, ReactNode][])
                : []),
            ['Features', d.features.length ? d.features.map((f) => f.replace(/_/g, ' ')).join(', ') : 'Basic'],
          ]}
        />
      </Section>

      <Section title="Why it is flagged">
        {t.flags.length === 0 ? (
          <p className="text-muted-foreground">Nothing is wrong with this team.</p>
        ) : (
          <ul className="space-y-2">
            {t.flags.map((f) => (
              <li key={f} className="flex items-start gap-3">
                <FlagChips flags={[f]} />
                <span className="text-muted-foreground">{flagLabels[f].hint}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Latest notifications">
        {d.notifications.length === 0 ? (
          <p className="text-muted-foreground">The team has not received any notifications.</p>
        ) : (
          <ul className="divide-y">
            {d.notifications.map((n) => (
              <li key={n.id} className="flex items-start justify-between gap-4 py-2">
                <span>{n.message}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{formatRelative(n.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  )
}

function MembersTab({ d }: { d: TeamDetail }) {
  if (d.members.length === 0) {
    return (
      <div className="panel">
        <EmptyState title="Nobody belongs to this team">The team has no members left. It can be deleted from the database once nobody needs it.</EmptyState>
      </div>
    )
  }
  return (
    <div className="panel overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Person</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Joined Metafor</TableHead>
            <TableHead>Last sign-in</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {d.members.map((m) => (
            <TableRow key={m.membership_id}>
              <TableCell>
                <Person name={m.user?.name} email={m.user?.email} uid={m.user_id} />
              </TableCell>
              <TableCell>
                <RoleBadge role={m.role} />
              </TableCell>
              <TableCell>
                {m.user ? (
                  <span className="inline-flex items-center gap-2">
                    {m.user.email_verified ? 'Verified' : <span className="text-warn">Not verified</span>}
                    {m.user.disabled && <Badge variant="destructive">Disabled</Badge>}
                  </span>
                ) : (
                  <span className="text-risk">Account no longer exists in Firebase</span>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">{formatDate(m.user?.created_at)}</TableCell>
              <TableCell className="text-muted-foreground">{formatRelative(m.user?.last_sign_in_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function Bars({ rows, labels, colors }: { rows: Breakdown[]; labels: Record<string, string>; colors?: (key: string) => string }) {
  const total = rows.reduce((n, r) => n + r.companies, 0)
  if (total === 0) return <p className="text-muted-foreground">No companies yet.</p>
  return (
    <ul className="space-y-3">
      {rows.map((r) => (
        <li key={r.key}>
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <span>{labels[r.key] ?? r.key}</span>
            <span className="figures text-muted-foreground">
              <span className="font-medium text-foreground">{formatNumber(r.companies)}</span> · {percent(r.companies / total)}
              {r.mrr > 0 && <> · {formatNumber(r.mrr)} MRR</>}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full" style={{ width: `${(r.companies / total) * 100}%`, background: colors?.(r.key) ?? 'var(--primary)' }} />
          </div>
        </li>
      ))}
    </ul>
  )
}

const statusColor = (k: string) => `var(--status-${k.replace(/_/g, '-')}, var(--primary))`

function DataTab({ d }: { d: TeamDetail }) {
  const t = d.team
  return (
    <div className="grid items-start gap-4 lg:grid-cols-2">
      <Section title="Companies by status" aside={<span className="figures text-muted-foreground">{formatNumber(t.companies)} companies</span>}>
        <Bars rows={d.data.by_status} labels={statusLabels} colors={statusColor} />
      </Section>
      <Section title="Where they came from">
        <Bars rows={d.data.by_source} labels={sourceLabels} />
      </Section>
      <Section title="Volume">
        <Facts
          rows={[
            ['Companies', formatNumber(t.companies)],
            ['History rows', formatNumber(t.snapshots)],
            ['Custom fields', formatNumber(d.data.custom_fields)],
            ['Custom field values', formatNumber(d.data.custom_field_values)],
            ['Newest data', formatRelative(t.last_data_at)],
            ['Own customers API', t.customers_api_url ? <MonoId key="u" value={t.customers_api_url} /> : 'No, uses the shared one'],
            ['Currency', t.currency],
          ]}
        />
      </Section>
    </div>
  )
}

function IntegrationsTab({ d }: { d: TeamDetail }) {
  const h = d.integrations.hubspot
  return (
    <div className="grid items-start gap-4 lg:grid-cols-2">
      <Section title="HubSpot">
        {h ? (
          <Facts
            rows={[
              ['Sync', <SyncStatus key="s" status={h.sync_status} />],
              ['Last successful sync', h.last_sync_success_date ? `${formatDateTime(h.last_sync_success_date)} (${formatRelative(h.last_sync_success_date)})` : 'Never'],
              ['Portal', h.hub_domain || '—'],
              ['Portal id', <MonoId key="id" value={h.hub_id ? String(h.hub_id) : ''} />],
              ['Connected', formatDate(h.connected_at)],
            ]}
          />
        ) : (
          <p className="text-muted-foreground">Not connected.</p>
        )}
      </Section>
      <Section title="Zapier">
        <p className={d.integrations.zapier ? '' : 'text-muted-foreground'}>{d.integrations.zapier ? 'An API key is active.' : 'Not connected.'}</p>
      </Section>
      <Section title="MCP keys" aside={<span className="text-muted-foreground">{d.integrations.mcp_keys.length}</span>}>
        {d.integrations.mcp_keys.length === 0 ? (
          <p className="text-muted-foreground">No keys. Keys are shown here by name only; the secret is never sent to this panel.</p>
        ) : (
          <ul className="divide-y">
            {d.integrations.mcp_keys.map((k) => (
              <li key={k.id} className="flex items-center justify-between gap-4 py-2">
                <span className="font-medium">{k.name || `Key ${k.id}`}</span>
                <span className="text-xs text-muted-foreground">created {formatDate(k.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  )
}

function BillingTab({ d }: { d: TeamDetail }) {
  const s = d.team.subscription
  const [changing, setChanging] = useState(false)
  return (
    <div className="space-y-4">
      <PlanDialog key={`${d.team.plan_id}-${s.expires_at}`} team={d.team} open={changing} onOpenChange={setChanging} />
      <Section
        title="Subscription"
        aside={
          <Button size="sm" variant="outline" onClick={() => setChanging(true)}>
            <Pencil /> Change plan
          </Button>
        }
      >
        {s.state === 'free' ? (
          <p className="text-muted-foreground">This team is on the Free plan and has no subscription.</p>
        ) : s.source === 'trial' ? (
          <Facts
            rows={[
              ['Plan', <PlanBadge key="p" planId={d.team.plan_id} name={d.team.plan_name} trial />],
              ['Granted', 'The 14-day trial every new team starts on, not billed'],
              ['State', <StateBadge key="s" state={s.state} />],
              [s.state === 'expired' ? 'Ended' : 'Ends', `${formatDate(s.expires_at)} (${formatRelative(s.expires_at)})`],
            ]}
          />
        ) : s.source === 'manual' ? (
          <Facts
            rows={[
              ['Plan', <PlanBadge key="p" planId={d.team.plan_id} name={d.team.plan_name} />],
              ['Granted', 'By hand, not billed through Stripe'],
              ['State', <StateBadge key="s" state={s.state} />],
              [s.state === 'expired' ? 'Ended' : 'Until', s.expires_at ? `${formatDate(s.expires_at)} (${formatRelative(s.expires_at)})` : 'No end date'],
            ]}
          />
        ) : (
          <Facts
            rows={[
              ['State', <StateBadge key="s" state={s.state} />],
              ['Price', `${formatMoney(s.price_cents)} per ${s.interval === 'yearly' ? 'year' : 'month'}`],
              [s.state === 'canceling' ? 'Ends' : s.state === 'expired' ? 'Ended' : 'Renews', formatDate(s.expires_at)],
              ['Stripe customer', <MonoId key="c" value={s.stripe_customer_id} />],
              [
                'In Stripe',
                <a key="l" className="inline-flex items-center gap-1 text-primary hover:underline" href={`https://dashboard.stripe.com/customers/${s.stripe_customer_id}`} target="_blank" rel="noreferrer">
                  Open customer <ExternalLink className="size-3" />
                </a>,
              ],
            ]}
          />
        )}
      </Section>

      <div className="panel overflow-x-auto">
        <div className="px-4 pt-4 font-semibold">Invoices</div>
        {d.invoices.length === 0 ? (
          <EmptyState title="No invoices stored">Invoices are saved from Stripe events as they arrive. Run the backfill command to import older ones.</EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Invoice</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.invoices.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell className="font-medium">{inv.number || <MonoId value={inv.stripe_invoice_id} />}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(inv.stripe_created_at)}</TableCell>
                  <TableCell>
                    <InvoiceStatus status={inv.status} attempts={inv.attempt_count} />
                  </TableCell>
                  <TableCell className="figures text-right">{formatMoney(inv.status === 'paid' ? inv.amount_paid : inv.amount_due, inv.currency)}</TableCell>
                  <TableCell>
                    {inv.hosted_invoice_url && (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                        <FileText className="size-3.5" /> View
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}

function AiTab({ d }: { d: TeamDetail }) {
  const ai = d.ai
  if (ai.chats === 0) {
    return (
      <div className="panel">
        <EmptyState title="The assistant hasn't been used">No conversations have been started in this team.</EmptyState>
      </div>
    )
  }
  return (
    <div className="grid items-start gap-4 lg:grid-cols-2">
      <Section title="Usage">
        <Facts
          rows={[
            ['Conversations', formatNumber(ai.chats)],
            ['Messages', formatNumber(ai.messages)],
            ['Tokens read', formatNumber(ai.input_tokens)],
            ['Tokens written', formatNumber(ai.output_tokens)],
            ['Last used', formatRelative(ai.last_used_at)],
          ]}
        />
      </Section>
      <Section title="By model">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Model</TableHead>
              <TableHead className="text-right">Messages</TableHead>
              <TableHead className="text-right">Tokens in / out</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ai.by_model.map((m) => (
              <TableRow key={m.model}>
                <TableCell className="font-mono text-xs">{m.model}</TableCell>
                <TableCell className="figures text-right">{formatNumber(m.messages)}</TableCell>
                <TableCell className="figures text-right">
                  {formatNumber(m.input_tokens)} / {formatNumber(m.output_tokens)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </div>
  )
}

export default function TeamDetailPage() {
  const { id } = useParams()
  const [search, setSearch] = useSearchParams()
  const { data, error, isPending, refetch } = useTeamDetail(id)
  const tab = (TABS.find((t) => t.id === search.get('tab'))?.id ?? 'overview') as TabId

  const back = (
    <Link to="/teams" className="mb-3 inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
      <ArrowLeft className="size-4" /> Teams
    </Link>
  )

  if (isPending)
    return (
      <>
        {back}
        <Skeleton className="mb-4 h-16 w-1/2" />
        <Skeleton className="h-72 w-full rounded-xl" />
      </>
    )
  if (error)
    return (
      <>
        {back}
        <div className="panel">
          <ErrorState error={error} onRetry={() => refetch()} />
        </div>
      </>
    )

  const t = data.team
  return (
    <TooltipProvider>
      {back}
      <div className="mb-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-2xl font-semibold">{t.name || `Team ${t.id}`}</h1>
          <PlanBadge planId={t.plan_id} name={t.plan_name} trial={t.subscription.source === 'trial'} />
          <StateBadge state={t.subscription.state} />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground">
          <span>Owner: {t.owner?.email || t.owner?.uid || 'unknown'}</span>
          <span>Last active {formatRelative(t.last_active_at)}</span>
          <span className="inline-flex items-center gap-1">
            Team id <MonoId value={String(t.id)} />
          </span>
        </div>
        {t.flags.length > 0 && (
          <div className="mt-3">
            <FlagChips flags={t.flags} max={8} />
          </div>
        )}
      </div>

      <Warnings warnings={data.warnings} />

      <div role="tablist" aria-label="Team sections" className="mb-4 flex gap-1 overflow-x-auto border-b">
        {TABS.map((x) => (
          <button
            key={x.id}
            role="tab"
            type="button"
            aria-selected={tab === x.id}
            onClick={() => setSearch((p) => { const n = new URLSearchParams(p); n.set('tab', x.id); return n }, { replace: true })}
            className={cn(
              '-mb-px h-9 shrink-0 border-b-2 px-3 font-medium',
              tab === x.id ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {x.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab d={data} />}
      {tab === 'members' && <MembersTab d={data} />}
      {tab === 'data' && <DataTab d={data} />}
      {tab === 'integrations' && <IntegrationsTab d={data} />}
      {tab === 'billing' && <BillingTab d={data} />}
      {tab === 'ai' && <AiTab d={data} />}
    </TooltipProvider>
  )
}
